/**
 * Abweichende Anschriften eines Geschäftspartners.
 *
 * Die Hauptanschrift steht im Partnerformular selbst; hier stehen
 * zusätzliche Liefer- und Rechnungsanschriften — die Baustelle, das
 * Zweiglager, die zentrale Rechnungsstelle.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from '../api/client';
import { useAuth } from '../auth';
import { useToast } from './toast';
import { Badge, Dialog, EmptyState, Field, LoadingBlock, Select, TextInput } from './ui';

export type PartnerAddress = {
  id: string;
  partnerId: string;
  addressType: 'delivery' | 'invoice';
  label: string | null;
  name: string;
  name2: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  country: string;
  isDefault: boolean;
};

const TYPE_LABELS: Record<string, string> = {
  delivery: 'Lieferanschrift',
  invoice: 'Rechnungsanschrift',
};

export function PartnerAddresses({ partnerId }: { partnerId: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const mayWrite = can('masterdata:write');

  const [editing, setEditing] = useState<PartnerAddress | 'neu' | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['partner-addresses', partnerId],
    queryFn: () => api.get<{ addresses: PartnerAddress[] }>(`/api/partners/${partnerId}/addresses`),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/partners/${partnerId}/addresses/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-addresses', partnerId] });
      toast.success('Die Anschrift wurde gelöscht.');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Löschen fehlgeschlagen.'),
  });

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2>Abweichende Anschriften</h2>
          <div className="faint" style={{ fontSize: 12.5 }}>
            Werden im Beleg zur Auswahl angeboten und dort als Kopie festgehalten
          </div>
        </div>
        {mayWrite ? (
          <button type="button" className="btn btn-sm" onClick={() => setEditing('neu')}>
            Anschrift hinzufügen
          </button>
        ) : null}
      </div>

      {isLoading ? <LoadingBlock /> : null}

      {data ? (
        data.addresses.length === 0 ? (
          <EmptyState
            title="Keine abweichenden Anschriften"
            hint="Lieferungen gehen an die Hauptanschrift des Partners."
          />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th className="shrink">Art</th>
                  <th className="shrink">Bezeichnung</th>
                  <th>Anschrift</th>
                  <th className="shrink"></th>
                  {mayWrite ? <th className="shrink"></th> : null}
                </tr>
              </thead>
              <tbody>
                {data.addresses.map((address) => (
                  <tr key={address.id}>
                    <td className="faint">{TYPE_LABELS[address.addressType]}</td>
                    <td style={{ fontWeight: 500 }}>{address.label ?? ''}</td>
                    <td>
                      <div>{address.name}</div>
                      {address.name2 ? <div className="faint">{address.name2}</div> : null}
                      <div className="muted">
                        {address.street}
                        {address.street && (address.postalCode || address.city) ? ', ' : ''}
                        {address.postalCode} {address.city}
                        {address.country !== 'DE' ? ` (${address.country})` : ''}
                      </div>
                    </td>
                    <td>{address.isDefault ? <Badge kind="accent">Vorgabe</Badge> : null}</td>
                    {mayWrite ? (
                      <td>
                        <div className="row" style={{ gap: 6 }}>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setEditing(address)}
                          >
                            Ändern
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => remove.mutate(address.id)}
                          >
                            Löschen
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {editing ? (
        <AddressDialog
          partnerId={partnerId}
          address={editing === 'neu' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function AddressDialog({
  partnerId,
  address,
  onClose,
}: {
  partnerId: string;
  address: PartnerAddress | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [form, setForm] = useState({
    addressType: address?.addressType ?? ('delivery' as 'delivery' | 'invoice'),
    label: address?.label ?? '',
    name: address?.name ?? '',
    name2: address?.name2 ?? '',
    street: address?.street ?? '',
    postalCode: address?.postalCode ?? '',
    city: address?.city ?? '',
    country: address?.country ?? 'DE',
    isDefault: address?.isDefault ?? false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        label: form.label || null,
        name2: form.name2 || null,
        street: form.street || null,
        postalCode: form.postalCode || null,
        city: form.city || null,
      };
      return address
        ? api.patch(`/api/partners/${partnerId}/addresses/${address.id}`, payload)
        : api.post(`/api/partners/${partnerId}/addresses`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-addresses', partnerId] });
      toast.success('Gespeichert.');
      onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        if (Object.keys(error.fieldErrors).length === 0) toast.error(error.message);
      } else {
        toast.error('Speichern fehlgeschlagen.');
      }
    },
  });

  return (
    <Dialog
      title={address ? 'Anschrift ändern' : 'Anschrift hinzufügen'}
      wide
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending || !form.name.trim()}
            onClick={() => {
              setErrors({});
              save.mutate();
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Art" span={4} required>
          <Select
            value={form.addressType}
            onChange={(value) => setForm({ ...form, addressType: value })}
            options={[
              { value: 'delivery', label: 'Lieferanschrift' },
              { value: 'invoice', label: 'Rechnungsanschrift' },
            ]}
          />
        </Field>
        <Field
          label="Bezeichnung"
          span={8}
          error={errors.label}
          hint="Erscheint in der Auswahl, z. B. „Baustelle Nord“"
        >
          <TextInput value={form.label} onChange={(value) => setForm({ ...form, label: value })} />
        </Field>

        <Field label="Name" span={6} required error={errors.name}>
          <TextInput
            value={form.name}
            onChange={(value) => setForm({ ...form, name: value })}
            invalid={Boolean(errors.name)}
            autoFocus
          />
        </Field>
        <Field label="Namenszusatz" span={6} error={errors.name2}>
          <TextInput value={form.name2} onChange={(value) => setForm({ ...form, name2: value })} />
        </Field>

        <Field label="Straße und Hausnummer" span={6} error={errors.street}>
          <TextInput value={form.street} onChange={(value) => setForm({ ...form, street: value })} />
        </Field>
        <Field label="PLZ" span={2} error={errors.postalCode}>
          <TextInput
            value={form.postalCode}
            onChange={(value) => setForm({ ...form, postalCode: value })}
          />
        </Field>
        <Field label="Ort" span={3} error={errors.city}>
          <TextInput value={form.city} onChange={(value) => setForm({ ...form, city: value })} />
        </Field>
        <Field label="Land" span={1} error={errors.country}>
          <TextInput
            value={form.country}
            onChange={(value) => setForm({ ...form, country: value.toUpperCase() })}
            maxLength={2}
          />
        </Field>
      </div>

      <label className="checkbox-row" style={{ marginTop: 14 }}>
        <input
          type="checkbox"
          checked={form.isDefault}
          onChange={(event) => setForm({ ...form, isDefault: event.target.checked })}
        />
        Als Vorgabe verwenden — wird bei neuen Belegen automatisch eingesetzt
      </label>
    </Dialog>
  );
}
